import { isUserLogin } from '@eureka/utils/account';
import {
    PsHttpService,
    type PatSnapCommonResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

/**
 * SAR API接口定义
 */
export interface SarApi {
    /**
     * 获取SAR文件列表
     */
    getSarFileList(agentJobId: string): Promise<SarFileListResponse>;

    /**
     * 获取SAR文件详情
     */
    getSarFileDetail(agentJobId: string, fileId: string): Promise<SarFileDetailResponse>;

    /**
     * 导出SAR数据
     */
    exportSarData(params: ExportSarDataParams): Promise<ExportSarDataResponse>;

    /**
     * 获取导出状态
     */
    getExportStatus(exportTaskId: string): Promise<GetExportStatusResponse>;
}

/**
 * SAR文件列表项
 */
export interface SarFileItem {
    file_group_id: string;
    file_id: string;
    file_name: string;
    status: 'PROCESSING' | 'SUCCESS' | 'FAILED'
}

/**
 * SAR文件列表响应
 */
export interface SarFileListResponse {
    item_status: SarFileItem[];
}

/**
 * SAR文件详情响应
 */
export interface SarFileDetailResponse {
    id: string;
    name: string;
    content: string;
}

export interface ExportSarDataParams {
    task_id: string;
    export_type: 'csv' | 'excel' | 'excel_merge';
    files: { file_id: string }[];
}

export interface ExportSarDataResponse {
    export_task_id: string;
}

export interface GetExportStatusResponse {
    status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
    export_file_path: string | null;
}

export const SarApi = Symbol('SarApi');

/**
 * SAR API实现
 */
@injectable()
export class SarApiImpl extends PsHttpService implements SarApi {
    get baseUrl() {
        return `${import.meta.env.VITE_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0`
    }

    get options() {
        return {
            baseURL: this.baseUrl,
        };
    }

    private async _getBaseUrl(path: string) {
        const isLogin = await isUserLogin()
        const base = isLogin ? '' : '/guest'
        return `${base}${path}`
    }

    /**
     * 获取SAR文件列表
     */
    async getSarFileList(agentJobId: string): Promise<SarFileListResponse> {
        // TODO: 替换为真实接口
        return this.post<PatSnapCommonResponseData<SarFileListResponse>>({
            url: await this._getBaseUrl('/file/group/status'),
            data: {
                file_group_id: agentJobId,
            },
        }).then((res) => {
            return res
        }) as Promise<SarFileListResponse>;
    }

    /**
     * 获取SAR文件详情
     */
    async getSarFileDetail(agentJobId: string, fileId: string): Promise<SarFileDetailResponse> {
        // TODO: 替换为真实接口
        return this.post<PatSnapCommonResponseData<SarFileDetailResponse>>({
            url: await this._getBaseUrl(`/eureka/sar/${agentJobId}/files/${fileId}`),
            data: {
                agent_job_id: agentJobId,
                file_id: fileId,
            },
        }).then((res) => {
            // Mock数据 - 待替换为真实接口
            return {
                id: fileId,
                name: `File ${fileId}.pdf`,
                content: 'File content placeholder',
            };
        }) as Promise<SarFileDetailResponse>;
    }

    async exportSarData(params: ExportSarDataParams): Promise<ExportSarDataResponse> {
        return this.post<PatSnapCommonResponseData<ExportSarDataResponse>>({
            url: await this._getBaseUrl('/dg/export'),
            data: params,
        }).then(res => res as unknown as ExportSarDataResponse);
    }

    async getExportStatus(exportTaskId: string): Promise<GetExportStatusResponse> {
        return this.get<PatSnapCommonResponseData<GetExportStatusResponse>>({
            url: await this._getBaseUrl('/dg/export/status'),
            params: { export_task_id: exportTaskId },
        }).then(res => res as unknown as GetExportStatusResponse);
    }
}
